import { z } from "zod";
import { localDateTimeSchema } from "./booking";
import { formatCents } from "./catalog";
import { normalizePhone } from "./clients";
import { type PublicTracking, bookingTrackingSchema } from "./marketing";
import type { OnlineConfirmation } from "./online";

/**
 * An event's own booking page: /<event-slug>. One person per booking, no
 * sign-in; they type everything in and are matched to their record.
 */

export interface EventPageView {
  slug: string;
  /** Marketing tags to load on this page; null when tracking is off. */
  tracking: PublicTracking | null;
  name: string;
  host: string;
  /** For the forms and payment-status links (the clinic's booking address). */
  clinicSlug: string;
  clinicName: string;
  /** Hero top (a logo replaces it), the small line under it, hero bottom (headline), and its sub-headline (intro). Each is left off when blank. */
  heroTop: string;
  cityLine: string;
  headline: string;
  intro: string;
  addOnsNote: string;
  logoUrl: string | null;
  heroUrl: string | null;
  heroFocus: { x: number; y: number } | null;
  bandUrl: string | null;
  /** "pattern" only when a strip is uploaded (otherwise it's a line). */
  heroEdge: "line" | "pattern" | "fade";
  edgeColor: string | null;
  /** A built-in font by name, or an uploaded one to load from url. */
  headingFont: { family: string; url: string | null } | null;
  bodyFont: { family: string; url: string | null } | null;
  colors: { background: string | null; card: string | null; text: string | null; heading: string | null; button: string | null };
  venueName: string;
  venueAddress: string;
  /** False until the event has a hero photo, an upcoming date, and a drip or an extra (or it's walk-ups only). */
  open: boolean;
  dates: { id: string; start: string; end: string; title: string; subtitle: string }[];
  /** regularCents: the usual price, shown struck through when the event's is lower. */
  drips: { id: string; name: string; description: string; priceCents: number; regularCents: number; depositCents: number; addOnIds: string[] }[];
  /** Booked with or without a drip (pick one); `title` is the heading they're listed under. */
  extras: { id: string; name: string; description: string; priceCents: number; regularCents: number }[];
  extrasTitle: string;
  /** Only with a drip; any number. In page order; `group` is the heading they're listed under. `service`: a service booked alongside the drip (a shot). */
  addOns: { id: string; name: string; note: string; priceCents: number; regularCents: number; group: string; service: boolean }[];
}

/** A drip (with its add-ons), an extra, or both. */
export const eventPageSlotsSchema = z.object({
  occurrenceId: z.string().uuid(),
  serviceId: z.string().uuid().nullable().default(null),
  extraId: z.string().uuid().nullable().default(null),
  addOnIds: z.array(z.string().uuid()).max(20).default([]),
});

export interface EventPageSlots {
  times: string[];
  totalCents: number;
  depositCents: number;
}

const required = (label: string) => z.string().trim().min(1, `${label} is required`);

export const eventPageBookingSchema = z.object({
  /** Marketing tracking cookies (only sent when the clinic has tracking on). */
  tracking: bookingTrackingSchema,
  occurrenceId: z.string().uuid(),
  start: localDateTimeSchema,
  serviceId: z.string().uuid().nullable().default(null),
  extraId: z.string().uuid().nullable().default(null),
  addOnIds: z.array(z.string().uuid()).max(20).default([]),
  person: z
    .object({
      firstName: required("First name").pipe(z.string().max(80)),
      lastName: required("Last name").pipe(z.string().max(80)),
      dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Enter your date of birth"),
      /** A known client (same name and birthday): use what's on file; only what's missing there is typed. */
      useOnFile: z.boolean().default(false),
      mobile: z.string().trim().max(30).default(""),
      email: z.string().trim().toLowerCase().max(254).default(""),
      address: z
        .object({
          line1: z.string().trim().max(120).default(""),
          line2: z.string().trim().max(120).default(""),
          city: z.string().trim().max(80).default(""),
          state: z.string().trim().max(40).default(""),
          postalCode: z.string().trim().max(12).default(""),
        })
        .default({ line1: "", line2: "", city: "", state: "", postalCode: "" }),
    })
    .superRefine((p, ctx) => {
      const issue = (path: (string | number)[], message: string) => ctx.addIssue({ code: "custom", path, message });
      if (!p.useOnFile) {
        if (!p.mobile) issue(["mobile"], "Mobile number is required");
        if (!p.email) issue(["email"], "Email is required");
        if (!p.address.line1) issue(["address", "line1"], "Street address is required");
        if (!p.address.city) issue(["address", "city"], "City is required");
        if (!p.address.state) issue(["address", "state"], "State is required");
        if (!p.address.postalCode) issue(["address", "postalCode"], "ZIP is required");
      }
      if (p.mobile && !normalizePhone(p.mobile)) issue(["mobile"], "Check your mobile number");
      if (p.email && !z.string().email().safeParse(p.email).success) issue(["email"], "Check your email address");
    })
    .transform((p) => ({ ...p, mobile: p.mobile ? normalizePhone(p.mobile)! : "" })),
  textConsent: z.literal(true, { message: "Tick that we can text you about your appointment" }),
  /** Required when there's a deposit (checked on booking). */
  depositAgreed: z.boolean().default(false),
  /** Missing from pages loaded before the forms tick was its own: the old single tick covered both. */
  formsAgreed: z.boolean().optional(),
});
/** Name and birthday typed on an event page: are they a client we know? */
export const eventPageKnownSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  dob: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
/** Only enough for them to recognize themselves: never their details. What's missing on file is asked for. */
export type EventPageKnown = { known: false } | { known: true; mobileLast4: string | null; needs: { mobile: boolean; email: boolean; address: boolean } };
export type EventPageBooking = z.infer<typeof eventPageBookingSchema>;

/** The two ticks, worded as shown, so what was agreed to is kept with the booking. */
export const eventPageTextConsent = (clinicName: string) => `Text me appointment times and reminders from ${clinicName}. Msg & data rates may apply. Reply STOP to opt out.`;
/** The deposit tick (only when there's a deposit), with its amount. */
export const eventPageDepositAck = (depositCents: number) => `I understand the ${formatCents(depositCents)} deposit is non-refundable and goes toward my total.`;
/** The forms tick (always). */
export const EVENT_PAGE_FORMS_ACK = "I'll fill out an intake form before my visit (we'll text it to me), and I may need a short video health screening.";
/** Under 18: shown before paying, and in their confirmation. */
export const MINOR_NOTICE = "A parent or guardian must be present for the appointment and the telehealth screening, if one is required.";

export type EventPageConfirmation = OnlineConfirmation & { minor: boolean };
